import { ROUTE_PATTERNS } from '@/shared/config/routes';
import { StaggerItem } from '@/shared/ui/animations';

import {
  AnalysisProseCard,
  AnalysisSectionLayout,
  PROSE_CARD_STAGGER_SLOTS,
  SectionBlocks,
  type SectionBlock,
} from '../../components';
import {
  useAnalysisFromContext,
  type AnalysisSectionProps,
} from '../../utils/useAnalysisFromContext';
import { hasContent } from '../../utils/hasContent';
import { CurrentPerformanceAssessment, MarketForces, TopBusinessChallenges } from './components';
import { useTranslation } from 'react-i18next';

// The Commercial Objective card leads the cascade from slot 0, then an empty slot
// lets it settle before each section swipes up in order.
const CARD_SETTLE_GAP = 1;
const sectionStagger = (order: number) => PROSE_CARD_STAGGER_SLOTS + CARD_SETTLE_GAP + 1 + order;

export const BusinessChallenge = ({
  analysis: analysisProp,
  exporting,
}: AnalysisSectionProps = {}) => {
  const { t } = useTranslation('analysis');
  const title = t('analysisResult.businessChallenge.businessChallenge');
  const analysis = useAnalysisFromContext(analysisProp);

  const businessChallenge = analysis.structuredData?.businessChallenge;
  const commercialObjective = businessChallenge?.commercialObjective;
  const macroFactors = businessChallenge?.macroFactors;
  const challenges = businessChallenge?.topChallenges;
  const strengths = businessChallenge?.strengths;
  const weaknesses = businessChallenge?.weaknesses;

  const blocks: SectionBlock[] = [
    {
      key: 'objective',
      hasData: hasContent(commercialObjective),
      node: (
        <AnalysisProseCard
          title={t('analysisResult.commercialObjective.commercialObjective')}
          description={commercialObjective ?? ''}
          descriptionVariant="statement"
          columns={1}
          staggerStart={0}
        />
      ),
    },
    {
      key: 'market-forces',
      hasData: hasContent([macroFactors?.headwinds, macroFactors?.tailwinds]),
      node: (
        <StaggerItem index={sectionStagger(0)} variant="swipeUp">
          <MarketForces macroFactors={macroFactors} />
        </StaggerItem>
      ),
    },
    {
      key: 'challenges',
      hasData: hasContent(challenges),
      node: (
        <StaggerItem index={sectionStagger(1)} variant="swipeUp">
          <TopBusinessChallenges challenges={challenges} />
        </StaggerItem>
      ),
    },
    {
      key: 'performance',
      hasData: hasContent([strengths, weaknesses]),
      node: (
        <StaggerItem index={sectionStagger(2)} variant="swipeUp">
          <CurrentPerformanceAssessment strengths={strengths} weaknesses={weaknesses} />
        </StaggerItem>
      ),
    },
  ];

  return (
    <AnalysisSectionLayout
      analysisId={analysis.id}
      brand={analysis.brandName}
      section={ROUTE_PATTERNS.IQ_SECTION_BUSINESS_CHALLENGE}
      title={title}
      subtitle={t('analysisResult.businessChallenge.strategicContextMarketForces', {
        brandName: analysis.brandName,
      })}
      exporting={exporting}
    >
      <SectionBlocks category={title} blocks={blocks} />
    </AnalysisSectionLayout>
  );
};
